import React, { useEffect, useRef } from 'react';
import { getAudioEngine } from '../../audio';

export interface AudioVisualizerProps {
  width?: number;
  height?: number;
  barColor?: string;
  isSpeaking?: boolean;
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  width = 140,
  height = 24,
  barColor = '#00f3ff',
  isSpeaking = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameId = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const engine = getAudioEngine();
    const analyser = engine.getAnalyserNode();

    const bufferLength = analyser ? analyser.frequencyBinCount : 32;
    const dataArray = new Uint8Array(bufferLength);

    const renderFrame = () => {
      animationFrameId.current = requestAnimationFrame(renderFrame);

      if (analyser) {
        analyser.getByteFrequencyData(dataArray);
      } else {
        // Fallback subtle idle wave
        for (let i = 0; i < bufferLength; i++) {
          dataArray[i] = Math.sin(Date.now() * 0.003 + i * 0.4) * 15 + 18;
        }
      }

      ctx.clearRect(0, 0, width, height);

      const numBars = 20;
      const barWidth = Math.max(2, (width / numBars) - 2);
      let x = 1;

      for (let i = 0; i < numBars; i++) {
        // Map bar index to frequency bin
        const binIndex = Math.floor((i / numBars) * (bufferLength / 2));
        let val = dataArray[binIndex] || 0;

        // If speaking, boost vocal presence frequencies
        if (isSpeaking) {
          const vocalMultiplier = i >= 4 && i <= 14 ? 1.4 : 1.1;
          val = Math.min(255, val * vocalMultiplier + Math.random() * 20);
        }

        const barHeight = Math.max(3, (val / 255) * height);
        const y = height - barHeight;

        // Gradient from cyan to violet
        const grad = ctx.createLinearGradient(0, height, 0, 0);
        if (isSpeaking) {
          grad.addColorStop(0, '#ff2a4b');
          grad.addColorStop(0.6, '#ffd600');
          grad.addColorStop(1, '#00f3ff');
        } else {
          grad.addColorStop(0, 'rgba(0, 243, 255, 0.35)');
          grad.addColorStop(0.7, '#00f3ff');
          grad.addColorStop(1, '#b388ff');
        }

        ctx.fillStyle = grad;
        ctx.fillRect(x, y, barWidth, barHeight);

        // Top glow cap
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x, y, barWidth, 1.5);

        x += barWidth + 2;
      }
    };

    renderFrame();

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [width, height, barColor, isSpeaking]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="audio-fft-canvas"
      title="Real-time Web Audio FFT Spectrum"
      aria-label="Real-time Web Audio Spectrum Visualizer"
    />
  );
};
